import { RadioGroup as BaseRadioGroup, Radio } from '@base-ui/react'
import clsx from 'clsx'
import type React from 'react'
import { type TrackConfig, trackInteraction } from './tracking'

export type RadioCardVariant = 'sea' | 'forest'

export interface RadioCardProps {
  value: boolean | number | string
  title: string
  subtitle?: string
  disabled?: boolean
  className?: string
  variant?: RadioCardVariant
  hideIndicator?: boolean
}

const VARIANT_CLASSES: Record<RadioCardVariant, string> = {
  sea: 'focus-visible:ring-sea data-[checked]:bg-sky-25 data-[checked]:border-sea',
  forest: 'focus-visible:ring-forest data-[checked]:bg-mint-25 data-[checked]:border-forest',
}

// The filled radio dot tracks the variant so it matches the card's selected
// accent (sea → sea, forest → forest) instead of always rendering sea.
const INDICATOR_CHECKED_CLASSES: Record<RadioCardVariant, string> = {
  sea: 'group-data-[checked]:border-sea group-data-[checked]:bg-sea',
  forest: 'group-data-[checked]:border-forest group-data-[checked]:bg-forest',
}

export function RadioCard({
  value,
  title,
  subtitle,
  disabled = false,
  className = '',
  variant = 'sea',
  hideIndicator = false,
  ...props
}: RadioCardProps &
  Omit<
    React.ComponentProps<typeof Radio.Root>,
    'value' | 'disabled' | 'className' | 'onChange' | 'onSelect'
  >) {
  return (
    <Radio.Root
      value={value}
      disabled={disabled}
      className={clsx(
        'group flex border-2 transition-colors cursor-pointer bg-white border-gray-300 hover:border-gray-400',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANT_CLASSES[variant],
        hideIndicator
          ? 'items-center justify-center h-10 px-4 rounded-lg'
          : 'items-start gap-2 p-4 rounded text-left',
        className
      )}
      {...props}
    >
      {!hideIndicator && (
        <div
          className={clsx(
            'w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 transition-colors flex-shrink-0 border-gray-300 bg-white',
            INDICATOR_CHECKED_CLASSES[variant]
          )}
        >
          <Radio.Indicator className="w-2 h-2 rounded-full bg-white" />
        </div>
      )}

      <div className="flex flex-col">
        <span className="text-sm font-medium leading-normal text-charcoal">{title}</span>
        {!hideIndicator && subtitle && (
          <span className="text-sm font-normal leading-normal text-charcoal-75">{subtitle}</span>
        )}
      </div>
    </Radio.Root>
  )
}

type BaseRadioGroupProps = React.ComponentProps<typeof BaseRadioGroup>
type BaseRadioGroupValueChangeHandler = NonNullable<BaseRadioGroupProps['onValueChange']>
type RadioGroupEventDetails = Parameters<BaseRadioGroupValueChangeHandler>[1]
type RadioGroupValue = boolean | number | string

export interface RadioCardGroupProps
  extends Omit<BaseRadioGroupProps, 'defaultValue' | 'onValueChange' | 'value'> {
  defaultValue?: RadioGroupValue
  value?: RadioGroupValue
  onValueChange?: (value: RadioGroupValue, eventDetails: RadioGroupEventDetails) => void
  track?: TrackConfig<{ value: RadioGroupValue }>
}

export function RadioGroup({ onValueChange, track, ...props }: RadioCardGroupProps) {
  const handleValueChange: BaseRadioGroupValueChangeHandler = (value, eventDetails) => {
    const nextValue = value as RadioGroupValue
    onValueChange?.(nextValue, eventDetails)
    trackInteraction(track, { value: nextValue })
  }

  return (
    <BaseRadioGroup
      onValueChange={onValueChange || track ? handleValueChange : undefined}
      {...props}
    />
  )
}
